import React from "react";
import { observer } from "mobx-react-lite";
import { searchStore } from "features/ProductSearch/SearchStore";
import ProductsListContainer from "../ProductsListContainer";
import ProductRowsList, { RowsListProps } from "../ProductRowsList";
import { buildingStore } from "store/Building/BuildingStore";
import {
  IconCopy,
  IconEdit,
  IconInfoCircle,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Button from "components/Button";
import { useNavigate } from "react-router";
import EmptyState from "../EmptyState";
import { RemoveStateProps } from "./CustomProducts";
import useToast from "hooks/useToast";
import ConfirmModal from "components/ConfirmModal";
import { InternalProductSearchOutput, ResultOUT } from "api-client";
import { AxiosError } from "axios";
import useComponentActions from "./useComponentActions";
import { ROUTES_ENUM } from "routes/consts";
import ProductDetailsModal from "features/ProductDetailsModal";
import { COEStore } from "features/ComponentEditor/ComponentEditorStore";

const CustomComponents = (props: RowsListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { duplicateComponent, editComponent } = useComponentActions();
  const [removeItemState, setRemoveItemState] = React.useState<
    RemoveStateProps | undefined
  >();

  React.useEffect(() => {
    buildingStore.getBuildingCalculationsStatus();
  }, []);

  const openComponentEditor = () => navigate(`${ROUTES_ENUM.COMPONENT_EDITOR}`);

  function shouldShowEmptyView() {
    return (
      !searchStore.productSearchLoading &&
      searchStore.searchResults.items.length === 0
    );
  }

  const removeComponent = async () => {
    if (!removeItemState?.id) return;
    const filteredItems = searchStore.searchResults.items.filter(
      (item) => item.id !== removeItemState?.id
    );
    await COEStore.deleteComponent(removeItemState?.id)
      .then(async () => {
        searchStore.setSearchResults({
          ...searchStore.searchResults,
          items: filteredItems,
        });
        toast(t("components.successMsgOnDelete"), "success");
      })
      .catch((err: AxiosError) => {
        const msg = `${
          ((err.response?.data as ResultOUT)?.message as ResultOUT)?.message ??
          err.message
        }`;
        toast(msg, "warning");
        console.error(err, "componentsDelete");
      });
  };

  const settingsItems = (
    product: InternalProductSearchOutput | null | undefined
  ) => {
    return [
      {
        name: (
          <div className="flex justify-between w-full">
            {t("mapping.editComponent")}
          </div>
        ),
        icon: <IconEdit width={18} className="mr-2" />,
        onClick: () => editComponent(product?.id),
        className: "text-gray-700",
        disabled:
          buildingStore.calculationsStatus.inProgress ||
          COEStore.openedSearchInCE ||
          product?.permissions?.can_edit === false,
      },
      {
        name: t("mapping.duplicateComponent"),
        icon: <IconCopy width={18} className="mr-2" />,
        onClick: () => duplicateComponent(product?.id),
        className: "text-gray-700",
        disabled:
          COEStore.openedSearchInCE ||
          product?.permissions?.can_duplicate === false,
      },
      {
        name: t("mapping.showDetails"),
        icon: <IconInfoCircle width={18} className="mr-2" />,
        onClick: () =>
          searchStore.setOpenDetails({ open: true, product: product }),
        className: "text-gray-700",
        disabled: false,
      },
      {
        name: t("mapping.removeComponent"),
        icon: <IconTrash width={18} className="mr-2" />,
        onClick: () => {
          if (product?.is_part_of_a_component) {
            setRemoveItemState({
              open: true,
              id: null,
              name: null,
            });
          } else {
            setRemoveItemState({
              open: true,
              id: product?.id,
              name: product?.name,
            });
          }
        },
        className: "text-red-700",
        disabled:
          buildingStore.calculationsStatus.inProgress ||
          COEStore.openedSearchInCE ||
          product?.permissions?.can_delete === false,
        hidden: product?.permissions?.can_delete === false,
      },
    ];
  };

  function headerBar() {
    return (
      <div className="h-[66px]">
        {searchStore.searchResults.items.length ? (
          <div
            className={clsx(
              "flex justify-between p-3 items-center text-base rounded-md shadow-md relative",
              "bg-gradient-to-r from-cyan-50 border border-gray-100"
            )}
          >
            <span className="font-normal text-gray-600 text-sm">
              {t("mapping.createComponentMsg")}
            </span>
            {searchStore.canCreate && (
              <Button
                width="fit-content"
                type="primary"
                leadingIcon={<IconPlus />}
                onClick={openComponentEditor}
              >
                {t("mapping.createComponent")}
              </Button>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {COEStore.openedSearchInCE ? null : headerBar()}
      <ProductsListContainer
        className={
          COEStore.openedSearchInCE ? "!mt-0" : "!mt-2 !h-[calc(100vh-440px)]"
        }
      >
        {shouldShowEmptyView() ? (
          <EmptyState />
        ) : (
          <ProductRowsList
            match={props.match}
            settingsItems={settingsItems}
            containerClassName="mt-5"
          />
        )}
      </ProductsListContainer>
      {removeItemState?.open && removeItemState?.id ? (
        <ConfirmModal
          type="warning"
          title={t("mapping.removeComponentTitle", {
            name: removeItemState.name,
          })}
          open={removeItemState?.open}
          close={() => setRemoveItemState(undefined)}
          onConfirm={removeComponent}
          confirmButtonTitle={t("mapping.removeProductBtn", {
            name: t("mapping.component"),
          })}
          descriptionMesage={t("mapping.removeComponentDesc")}
          containerClassName="!min-w-[600px]"
          buttonClassName="bg-red-600 hover:!bg-red-700"
        ></ConfirmModal>
      ) : null}
      {removeItemState?.open && !removeItemState.id ? (
        <ConfirmModal
          type="warning"
          title={t("mapping.removeProductForbidenTitle", {
            name: t("mapping.component"),
          })}
          open={removeItemState?.open}
          close={() => setRemoveItemState(undefined)}
          hideCancel
          onConfirm={() => setRemoveItemState(undefined)}
          confirmButtonTitle={t("mapping.closeNote")}
          descriptionMesage={t("components.cannotDeleteComponentMsg")}
          containerClassName="!min-w-[500px]"
        ></ConfirmModal>
      ) : null}
      <ProductDetailsModal
        productId={searchStore.openDetails.product?.id}
        productName={searchStore.openDetails.product?.name}
        productType={searchStore.openDetails.product?.type}
        open={searchStore.openDetails.open}
        close={() =>
          searchStore.setOpenDetails({ open: false, product: undefined })
        }
      />
    </>
  );
};

export default observer(CustomComponents);
